import { API_ENDPOINTS } from '../config/apiEndpoints';
import { API_CONFIG } from '../config/api';

function uuidv4() {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
    const r = (Math.random() * 16) | 0,
      v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

const BASE_URL = process.env.REACT_APP_API_BASE_URL;

// ✅ Use centralized token key constant
const getToken = () => localStorage.getItem(API_CONFIG.AUTH.TOKEN_KEY);

const fetchWithAuth = async (url: string, options: any = {}) => {
  const token = getToken();
  const headers = {
    ...options.headers,
    Authorization: `Bearer ${token}`,
    accept: 'application/json',
  };
  return fetch(url, { ...options, headers });
};

// --- Main service object ---
export const jobDescriptionService = {
  getAll: async (
    params: {
      role?: string;
      emailID?: string;
      page?: number;
      size?: number;
      [key: string]: any;
      type?: string;
    } = {}
  ) => {
    const token = getToken();

    if (!token) {
      throw new Error('No authentication token found. Please log in again.');
    }

    const userRole =
      params.role ||
      localStorage.getItem('user_role') ||
      JSON.parse(localStorage.getItem('user') || '{}').role ||
      'sme';

    console.log('User role for job API call:', userRole);

    const urlParams = new URLSearchParams();

    if (params.page !== undefined && params.page > 0) {
      urlParams.append('page', params.page.toString());
    } else {
      urlParams.append('page', '1');
    }

    if (params.size !== undefined && params.size > 0) {
      urlParams.append('size', params.size.toString());
    } else {
      urlParams.append('size', '10');
    }

    if (userRole !== 'hr' && userRole !== 'admin') {
      const currentUserEmail = params.emailID || localStorage.getItem('user_email');
      if (currentUserEmail) {
        urlParams.append('emailID', currentUserEmail);
      }
    } else {
      console.log('HR/Admin user - not including emailID parameter');
    }
    urlParams.append('type', params.type || 'jd');

    const url = `${BASE_URL}${API_ENDPOINTS.JOBS.LIST}?${urlParams.toString()}`;

    try {
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
          accept: 'application/json',
        },
      });

      if (!response.ok) {
        const errorText = await response.text().catch(() => 'Unknown error');
        console.error('Jobs API Error:', {
          status: response.status,
          statusText: response.statusText,
          url: url,
          params: params,
          error: errorText,
        });

        if (response.status === 404) {
          throw new Error('Jobs endpoint not found. Please check the API configuration.');
        } else if (response.status === 401) {
          throw new Error('Authentication failed. Please log in again.');
        } else if (response.status === 400) {
          throw new Error(`Invalid request parameters: ${errorText}`);
        } else {
          throw new Error(
            `Failed to fetch jobs: ${response.status} ${response.statusText} - ${errorText}`
          );
        }
      }

      const data = await response.json();
      console.log('Jobs API Response:', {
        total: data.total,
        page: data.page,
        size: data.size,
        totalPages: data.totalPages,
        jobsCount: data.jobs?.length || 0,
      });

      return {
        jobs: data.jobs || [],
        total: data.total || 0,
        page: data.page || 1,
        size: data.size || 10,
        pages: data.totalPages || Math.ceil((data.total || 0) / (data.size || 10)),
      };
    } catch (error) {
      console.error('Error in jobDescriptionService.getAll:', error);
      throw error;
    }
  },

  getResumesForJD: async (jobId: string, page = 1, size = 100) => {
    const token = getToken();

    const endpointsToTry = [
      `${BASE_URL}${API_ENDPOINTS.RESUMES.LIST}?job_id=${encodeURIComponent(jobId)}&page=${page}&size=${size}`,
      `${BASE_URL}${API_ENDPOINTS.RESUMES.LIST}?jobId=${jobId}&page=${page}&size=${size}`,
    ];

    console.log('Fetching resumes for JD:', jobId);

    for (const endpoint of endpointsToTry) {
      try {
        console.log(`Trying endpoint: ${endpoint}`);

        const response = await fetch(endpoint, {
          headers: {
            Authorization: `Bearer ${token}`,
            accept: 'application/json',
          },
        });

        if (response.ok) {
          const data = await response.json();
          console.log(`✅ Success with: ${endpoint}`, data);
          return data;
        } else {
          console.log(`❌ Failed: ${endpoint} - ${response.status}`);
        }
      } catch (error) {
        console.log(`💥 Error: ${endpoint} - ${error}`);
      }
    }

    throw new Error('No working endpoint found for fetching JD resumes');
  },

  add: async jdData => {
    const response = await fetchWithAuth(`${BASE_URL}${API_ENDPOINTS.JOBS.CREATE}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        ...jdData,
        event: jdData.event || false,
      }),
    });
    if (!response.ok) throw new Error('Failed to create JD');
    return response.json();
  },

  update: async (id, changes) => {
    const token = getToken();

    const endpointsToTry = [`${BASE_URL}${API_ENDPOINTS.JOBS.UPDATE}/${id}`];

    console.log('Updating JD:', { id, changes });

    for (const endpoint of endpointsToTry) {
      try {
        console.log(`Trying endpoint: ${endpoint}`);

        const response = await fetch(endpoint, {
          method: 'PUT',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
            accept: 'application/json',
          },
          body: JSON.stringify(changes),
        });

        if (response.ok) {
          const data = await response.json();
          console.log(`✅ Success updating JD with: ${endpoint}`);
          return data;
        } else {
          console.log(`❌ Failed: ${endpoint} - ${response.status}`);
        }
      } catch (error) {
        console.log(`💥 Error: ${endpoint} - ${error}`);
      }
    }

    throw new Error('No working endpoint found for updating JD');
  },

  delete: async jobId => {
    const response = await fetch(`${BASE_URL}${API_ENDPOINTS.JOBS.DELETE}/${jobId}`, {
      method: 'DELETE',
      headers: {
        Authorization: `Bearer ${getToken()}`,
        accept: 'application/json',
      },
    });
    if (!response.ok) throw new Error('Failed to delete JD');
    return response.status === 204;
  },

  checkDuplicate: async ({ title, location, excludeId }) => {
    const data = await jobDescriptionService.getAll();
    const all = data.jobs || [];
    const duplicate = all.find(
      jd =>
        jd.title.trim().toLowerCase() === title.trim().toLowerCase() &&
        jd.location.trim().toLowerCase() === location.trim().toLowerCase() &&
        (!excludeId || jd.id !== excludeId)
    );
    return {
      isDuplicate: !!duplicate,
      duplicateStatus: duplicate ? 'active' : 'inactive',
      duplicateId: duplicate ? duplicate.id : null,
    };
  },

  uploadResumes: async (jobId: string, files: File[], jobSummary = '', jobDescription = '') => {
    const formData = new FormData();
    files.forEach(file => {
      formData.append('files', file, file.name);
    });
    formData.append('job_id', jobId);
    formData.append('job_summary', jobSummary);
    formData.append('job_description', jobDescription);

    const response = await fetchWithAuth(`${BASE_URL}${API_ENDPOINTS.RESUMES.BULK_UPLOAD}`, {
      method: 'POST',
      body: formData,
    });

    if (!response.ok) {
      let errorMsg = 'Failed to upload resumes.';
      try {
        const errJson = await response.json();
        if (errJson && errJson.failed_files && errJson.failed_files.length > 0) {
          errorMsg = errJson.failed_files.map(f => `${f.filename}: ${f.error}`).join('\n');
        }
      } catch {}
      throw new Error(errorMsg);
    }
    return response.json();
  },

  uploadResume: async (resumeData: any) => {
    const token = getToken();
    const response = await fetch(`${BASE_URL}${API_ENDPOINTS.RESUMES.CREATE}`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(resumeData),
    });
    if (!response.ok) {
      let errorMsg = 'Failed to create resume.';
      try {
        const errJson = await response.json();
        if (errJson && errJson.detail) {
          errorMsg = errJson.detail.map((d: any) => d.msg).join('; ');
        }
      } catch {}
      throw new Error(errorMsg);
    }
    return response.json();
  },

  deleteResume: async (resumeId: string) => {
    const token = getToken();
    const res = await fetch(`${BASE_URL}${API_ENDPOINTS.RESUMES.DELETE}/${resumeId}`, {
      method: 'DELETE',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    if (!res.ok) throw new Error('Failed to delete resume');
    if (res.status === 204) return true;
    return res.json();
  },

  updateResumeStatus: async (resumeId, newStatus) => {
    const token = getToken();
    const endpointsToTry = [
      {
        url: `${BASE_URL}${API_ENDPOINTS.RESUMES.UPDATE_STATUS}/${resumeId}`,
        method: 'PATCH',
        body: { status: newStatus },
      },
      {
        url: `${BASE_URL}${API_ENDPOINTS.RESUMES.UPDATE}/${resumeId}`,
        method: 'PUT',
        body: { status: newStatus },
      },
    ];

    console.log('Updating resume status:', { resumeId, newStatus });

    for (const { url, method, body } of endpointsToTry) {
      try {
        console.log(`Trying endpoint: ${url}`, body);

        const response = await fetch(url, {
          method: method,
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
            accept: 'application/json',
          },
          body: body ? JSON.stringify(body) : undefined,
        });

        if (response.ok) {
          const data = await response.json();
          console.log(`✅ Success updating resume status with: ${url}`);
          return data;
        } else {
          console.log(`❌ Failed: ${url} - ${response.status}`);
        }
      } catch (error) {
        console.log(`💥 Error: ${url} - ${error}`);
      }
    }

    throw new Error('No working endpoint found for updating resume status');
  },

  // for updating job status
  updateJobStatus: async (jobId: string, newStatus: string) => {
    const token = getToken();
    const url = `${BASE_URL}${API_ENDPOINTS.JOBS.UPDATE_STATUS}/${jobId}`;

    console.log('Updating job status:', { jobId, newStatus });

    try {
      const response = await fetch(url, {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
          accept: 'application/json',
        },
        body: JSON.stringify({ status: newStatus }),
      });

      if (response.ok) {
        const data = await response.json();
        console.log(`✅ Success updating job status`);
        return data;
      } else {
        console.log(`❌ Failed: ${response.status}`);
        throw new Error(`Failed to update job status: ${response.status}`);
      }
    } catch (error) {
      console.log(`💥 Error: ${error}`);
      throw error;
    }
  },

  getResumeDetails: async (resumeId: string) => {
    const response = await fetchWithAuth(`${BASE_URL}${API_ENDPOINTS.RESUMES.GET}/${resumeId}`);
    if (!response.ok) throw new Error('Failed to fetch resume details');
    return response.json();
  },

  createResume: async (resumeData: any) => {
    const token = getToken();
    const response = await fetch(`${BASE_URL}${API_ENDPOINTS.RESUMES.CREATE}`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(resumeData),
    });
    if (!response.ok) {
      let errorMsg = 'Failed to create resume.';
      try {
        const errJson = await response.json();
        if (errJson && errJson.detail) {
          errorMsg = errJson.detail.map((d: any) => d.msg).join('; ');
        }
      } catch {}
      throw new Error(errorMsg);
    }
    return response.json();
  },

  updateResume: async (resumeId: string, changes: any) => {
    const token = getToken();
    const response = await fetch(`${BASE_URL}${API_ENDPOINTS.RESUMES.UPDATE}/${resumeId}`, {
      method: 'PUT',
      headers: {
        Authorization: `Bearer ${token}`,
        accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(changes),
    });
    if (!response.ok) throw new Error('Failed to update resume');
    return response.json();
  },

  assignSME: async (jobId: string, smeUsername: string): Promise<any> => {
    const token = getToken();

    console.log('Assigning SME to Job:', { jobId, smeUsername });

    try {
      console.log('Using correct API endpoint and payload format');

      const response = await fetch(`${BASE_URL}${API_ENDPOINTS.JOBS.ASSIGN_SME}`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
          accept: 'application/json',
        },
        body: JSON.stringify({
          job_id: jobId,
          sme: smeUsername,
        }),
      });

      console.log('Response status:', response.status);

      if (response.ok) {
        const result = await response.json();
        console.log('✅ SUCCESS:', result);
        return result;
      } else {
        const errorText = await response.text().catch(() => 'Unknown error');
        console.log('❌ Failed:', response.status, errorText);
        throw new Error(
          `Failed to assign SME: ${response.status} ${response.statusText} - ${errorText}`
        );
      }
    } catch (error) {
      console.log('💥 Error:', error);
      throw error;
    }
  },

  // for assigning panel to resume
  assignPanelToResume: async (resumeData: any) => {
    const token = getToken();

    const response = await fetch(`${BASE_URL}${API_ENDPOINTS.RESUMES.ASSIGN_PANEL}`, {
      method: 'PUT',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
        accept: 'application/json',
      },
      body: JSON.stringify(resumeData),
    });

    if (!response.ok) throw new Error('Failed to assign panel to resume');
    return response.json();
  },

  // for getting bulk upload status
  getBulkUploadStatus: async (jobId: string) => {
    const token = getToken();

    const response = await fetch(
      `${BASE_URL}${API_ENDPOINTS.RESUMES.BULK_UPLOAD_STATUS}/${jobId}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          accept: 'application/json',
        },
      }
    );

    if (!response.ok) throw new Error('Failed to fetch bulk upload status');
    return response.json();
  },

  //for downloading resume
  downloadResume: async (resumeId: string) => {
    const token = getToken();

    const response = await fetch(`${BASE_URL}${API_ENDPOINTS.RESUMES.DOWNLOAD}/${resumeId}`, {
      headers: {
        Authorization: `Bearer ${token}`,
        accept: 'application/json',
      },
    });

    if (!response.ok) throw new Error('Failed to download resume');
    return response.blob();
  },

  // for previewing resume
  previewResume: async (resumeId: string) => {
    const token = getToken();

    // ✅ Using API_ENDPOINTS constant
    const response = await fetch(`${BASE_URL}${API_ENDPOINTS.RESUMES.PREVIEW}/${resumeId}`, {
      headers: {
        Authorization: `Bearer ${token}`,
        accept: 'application/json',
      },
    });

    if (!response.ok) throw new Error('Failed to preview resume');
    return response.json();
  },

  //for getting jobs by user email
  getJobsByUserEmail: async (userEmail: string) => {
    const token = getToken();

    const response = await fetch(
      `${BASE_URL}${API_ENDPOINTS.JOBS.GET_BY_USER_EMAIL}/${userEmail}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          accept: 'application/json',
        },
      }
    );

    if (!response.ok) throw new Error('Failed to fetch jobs by user email');
    return response.json();
  },

  getById: async (jobId: string) => {
    const token = getToken();
    const response = await fetch(`${BASE_URL}${API_ENDPOINTS.JOBS.GET}/${jobId}`, {
      headers: {
        Authorization: `Bearer ${token}`,
        accept: 'application/json',
      },
    });
    if (!response.ok) throw new Error('Failed to fetch JD details');
    return response.json();
  },

  getActiveSMEs: async () => {
    const token = getToken();
    const res = await fetch(
      `${BASE_URL}${API_ENDPOINTS.USERS.LIST}?role=sme&page=1&size=100&active_only=true`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return res.json();
  },

  getActiveUsersByRole: async (role: string) => {
    const token = getToken();
    const res = await fetch(
      `${BASE_URL}${API_ENDPOINTS.USERS.LIST}?role=${role}&page=1&size=100&active_only=true`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return res.json();
  },

  getResumesByJD: async (jobId: string) => {
    const token = getToken();
    const res = await fetch(`${BASE_URL}${API_ENDPOINTS.RESUMES.LIST}?job_id=${jobId}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return res.json();
  },

  getJobStatusList: async (): Promise<any> => {
    const token = getToken();

    try {
      console.log('Fetching job status list...');
      const response = await fetch(`${BASE_URL}${API_ENDPOINTS.JOBS.STATUS_LIST}`, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
          accept: 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(
          `Failed to fetch job status list: ${response.status} ${response.statusText}`
        );
      }

      const data = await response.json();
      console.log('Job status list response:', data);

      if (!data.success) {
        throw new Error(data.message || 'Failed to retrieve job statuses');
      }

      return data.data;
    } catch (error) {
      console.error('Error fetching job status list:', error);
      throw error;
    }
  },

  async updateJobDescription(jobId: string, jobData: any): Promise<any> {
    try {
      const response = await fetch(`${BASE_URL}${API_ENDPOINTS.JOBS.UPDATE}/${jobId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getToken()}`,
        },
        body: JSON.stringify(jobData),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.detail || errorData.message || `HTTP error! status: ${response.status}`
        );
      }

      return await response.json();
    } catch (error) {
      console.error('Error updating job description:', error);
      throw error;
    }
  },
};

// Single delete
export const deleteJD = async (jobId: string) => {
  const token = getToken();
  const response = await fetch(`${BASE_URL}${API_ENDPOINTS.JOBS.DELETE}/${jobId}`, {
    method: 'DELETE',
    headers: {
      Authorization: `Bearer ${token}`,
      accept: 'application/json',
    },
  });
  if (!response.ok) throw new Error('Failed to delete JD');
  return response.json();
};
